import { el } from "./dom";
import { MateriaFx, prefersReducedMotion } from "./fx/materia";
import { icon } from "./icons";
import {
  createPaletteFromColors,
  getPalette,
  PANTONE_PALETTES,
  type ImportedColor,
  type Palette,
  type Swatch,
} from "./pantone-palettes";
import { decodeImagePixels, extractColorsFromImage, parseACO, parseASE } from "./pantone-importers";

/**
 * Rueda de color Pantone flotante.
 *
 * Portado del `PantoneWheelOverlay` (React) del estudio de referencia a la base
 * sin framework de drawi. Es un sistema de color aparte del selector normal: un
 * disco flotante de sectores que se arrastra para girar (con inercia), un nucleo
 * central que muestra el color activo y hace de tirador para mover la rueda, y un
 * anillo de degradado con matices y grises del color activo.
 *
 * En vez de un SVG por muestra (como la version React) se dibuja todo en un solo
 * SVG con un grupo que rota: el arrastre solo toca un `transform`, no reconstruye
 * el DOM, asi que gira fluido aunque haya un par de cientos de sectores.
 *
 * El color se emite por `onColorSelect`; el resto (posicion, rotacion, paleta) se
 * guarda en localStorage para reencontrar la rueda donde se dejo.
 */

const SVG_NS = "http://www.w3.org/2000/svg";
// Alto (grosor radial) de cada nivel de color. Con RING_DEPTH igual a este valor
// en pantone-palettes.ts los aros quedan pegados (radios contiguos).
const RING_THICKNESS = 30;
// Sin hueco angular: las muestras de un mismo anillo se tocan y no se ve el fondo
// entre ellas (rueda continua, "sin separaciones").
const GAP = 0;
// Radio INTERIOR del anillo de degradado (escala de grises + tintes). Es el
// valor para alejar del centro el aro de gris: subirlo lo separa del hub, igual
// que START_RADIUS (pantone-palettes.ts) hace con la corona de colores.
const GRAD_RADIUS = 106;
// Mismo alto que un nivel de color (RING_THICKNESS): la escala de grises interior
// deja de sobresalir en grosor frente al resto de anillos.
const GRAD_THICKNESS = 24;
const GRAD_SEGMENTS = 16;
const VIEW = 460; // medio lado del viewBox del SVG de anillos
const POS_KEY = "drawi.pantone.pos";
const ROT_KEY = "drawi.pantone.rot";
const PAL_KEY = "drawi.pantone.palette";
const PIN_KEY = "drawi.pantone.pinned";
const LOCK_KEY = "drawi.pantone.locked";

// Pulsación larga sobre el hub: mismos números que el menú radial (arrancar
// chips). Mantener el círculo del medio >3 s BLOQUEA/DESBLOQUEA la rueda: estando
// bloqueada, el clic fuera ya no contrae los niveles (solo el clic manual en el
// hub los contrae/expande) y sigue así hasta otra pulsación larga.
const LONG_PRESS_MS = 3000;
const LONG_PRESS_MOVE_TOL = 8;

// --------------------------------------------------------------- geometria

interface Pt {
  x: number;
  y: number;
}

/** Polar a cartesiano con 0 grados arriba (las 12 en punto) y giro horario. */
const polar = (r: number, deg: number): Pt => {
  const a = ((deg - 90) * Math.PI) / 180;
  return { x: r * Math.cos(a), y: r * Math.sin(a) };
};

/** Gira la etiqueta para que se lea derecha en la mitad izquierda de la rueda. */
const textRotation = (deg: number): number => {
  let n = deg % 360;
  if (n < 0) n += 360;
  return n > 90 && n < 270 ? n + 180 : n;
};

/**
 * Path SVG de un sector de corona (donut slice).
 * - `gap`: separacion ANGULAR entre sectores vecinos del mismo anillo.
 * - `radialGap`: separacion RADIAL entre anillos concentricos (por defecto = gap).
 *   Con 0 los anillos se tocan (pegados), sin la linea fina entre niveles.
 * - `refRadius`: radio de referencia para convertir `gap` (px) a grados. Pasando
 *   el MISMO valor a todos los anillos, el hueco angular es identico en grados y
 *   los bordes entre sectores caen en lineas radiales rectas —sin el escalonado
 *   que salia al calcular el angulo con el radio interior de cada anillo—.
 */
const annularSector = (
  inner: number,
  outer: number,
  start: number,
  end: number,
  gap: number,
  radialGap = gap,
  refRadius = inner,
): string => {
  const ag = (gap / refRadius) * (180 / Math.PI);
  const s = start + ag / 2;
  const e = end - ag / 2;
  const ir = inner + radialGap / 2;
  const or = outer - radialGap / 2;
  const so = polar(or, e);
  const eo = polar(or, s);
  const si = polar(ir, e);
  const ei = polar(ir, s);
  const large = e - s <= 180 ? "0" : "1";
  return [
    "M", so.x, so.y,
    "A", or, or, 0, large, 0, eo.x, eo.y,
    "L", ei.x, ei.y,
    "A", ir, ir, 0, large, 1, si.x, si.y,
    "Z",
  ].join(" ");
};

const svgEl = <K extends keyof SVGElementTagNameMap>(
  tag: K,
  attrs: Record<string, string | number> = {},
): SVGElementTagNameMap[K] => {
  const node = document.createElementNS(SVG_NS, tag);
  for (const [k, v] of Object.entries(attrs)) node.setAttribute(k, String(v));
  return node;
};

// --------------------------------------------------------------- color util

const contrastText = (hex: string): string => {
  const h = hex.replace("#", "");
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  return (r * 299 + g * 587 + b * 114) / 1000 >= 128 ? "#171a20" : "#ffffff";
};

const lerpHex = (a: string, b: string, t: number): string => {
  const pa = a.replace("#", "");
  const pb = b.replace("#", "");
  const mix = (i: number): string => {
    const va = parseInt(pa.slice(i, i + 2), 16);
    const vb = parseInt(pb.slice(i, i + 2), 16);
    return Math.round(va + (vb - va) * t).toString(16).padStart(2, "0");
  };
  return `#${mix(0)}${mix(2)}${mix(4)}`.toUpperCase();
};

const loadPos = (): Pt => {
  try {
    const s = localStorage.getItem(POS_KEY);
    if (s) return JSON.parse(s);
  } catch {
    /* valor por defecto abajo */
  }
  // Posición por defecto: esquina inferior derecha
  return { x: window.innerWidth - 140, y: window.innerHeight - 140 };
};

// -------------------------------------------------------------------- clase

export class PantoneWheel {
  readonly el: HTMLElement;

  private container: HTMLElement;
  private ringsSvg: SVGSVGElement;
  private rotGroup: SVGGElement;
  private gradientSvg: SVGSVGElement;
  private hubBtn: HTMLButtonElement;
  private hubName: HTMLElement;
  private hubHex: HTMLElement;
  /** Mitad inferior del hub (estilo Photoshop): muestra el color secundario y,
      al pulsarla, lo intercambia con el activo. Es un <span> (no <button>) para
      poder vivir DENTRO del botón del hub sin anidar botones. */
  private secondaryBtn: HTMLElement;
  private pinDot: HTMLElement;
  private library: HTMLElement;

  private onColorSelect: (hex: string) => void;
  /** Intercambia color activo ⇄ secundario (lo resuelve la App, igual que la X). */
  private onSwap: () => void;

  /** Color activo reflejado del editor: el hub lo pinta en reposo. Lo mantiene al
      día `reflect()` en cada cambio de estado. */
  private primaryHex = "#262626";

  private visible = false;
  private expanded = false;
  private pinned = localStorage.getItem(PIN_KEY) !== "false"; // activado por defecto
  // Bloqueo por pulsación larga (>3 s): mientras esté activo, el clic fuera NO
  // contrae los niveles; solo el clic manual en el hub los contrae/expande.
  // Persiste hasta otra pulsación larga (no lo quita un clic normal).
  private locked = localStorage.getItem(LOCK_KEY) === "true";
  private position: Pt = loadPos();
  private rotation = Number(localStorage.getItem(ROT_KEY)) || 0;
  private paletteId = localStorage.getItem(PAL_KEY) || "universal";
  private palette: Palette;
  private customPalettes: Palette[] = [];
  private collapseTimer = 0;
  private hideTimer = 0;
  private worker: Worker | null = null;

  /** Temporizador y bandera de la pulsación larga (fijar/cerrar la rueda). */
  private longPressTimer = 0;
  private longPressFired = false;

  /** Partículas (metaball) que forman/deshacen el hub. Sistema compartido. */
  private fx = new MateriaFx({ coreSize: 92, reach: 74, gatherMs: 380, scatterMs: 300 });
  private fxTimer = 0;

  private active: Swatch | null = null;

  // Inercia del giro.
  private velocity = 0;
  private lastAngle = 0;
  private rotating = false;
  private raf = 0;

  private onDocDown: (e: PointerEvent) => void;

  constructor(onColorSelect: (hex: string) => void, onSwap: () => void) {
    this.onColorSelect = onColorSelect;
    this.onSwap = onSwap;
    this.palette = this.resolvePalette(this.paletteId);

    this.rotGroup = svgEl("g");
    this.ringsSvg = svgEl("svg", {
      class: "pw-rings",
      viewBox: `${-VIEW} ${-VIEW} ${VIEW * 2} ${VIEW * 2}`,
    });
    this.ringsSvg.appendChild(this.rotGroup);

    // El viewBox (1px/unidad, ver .pw-gradient en CSS) deja margen hasta radio
    // 130 para que el aro de gris pueda alejarse (GRAD_RADIUS) sin recortarse.
    this.gradientSvg = svgEl("svg", { class: "pw-gradient", viewBox: "-130 -130 260 260" });

    this.hubName = el("span", { class: "pw-hub-name" });
    this.hubHex = el("span", { class: "pw-hub-hex" });
    this.pinDot = el("span", { class: "pw-pin", title: "Fijar (que no se cierre al hacer clic fuera)" });
    // Mitad inferior del hub: el corte con esquinas superiores redondeadas separa
    // el primario (arriba) del secundario (abajo). Un <span role="button"> para no
    // anidar botones dentro del botón del hub.
    this.secondaryBtn = el("span", {
      class: "pw-hub-secondary",
      role: "button",
      title: "Color secundario · clic para intercambiar (X)",
    });
    // materia-blob: el hub ondula como materia viva, no un círculo perfecto.
    this.hubBtn = el("button", { class: "pw-hub materia-blob", type: "button", title: "Arrastra para mover · clic para abrir/cerrar · mantén pulsado para fijar/cerrar · Ctrl+clic: paletas" }, [
      this.secondaryBtn,
      el("span", { class: "pw-hub-label" }, [this.hubName, this.hubHex]),
      this.pinDot,
    ]);

    this.library = el("div", { class: "pw-library", role: "menu" });
    this.library.hidden = true;

    this.container = el("div", { class: "pw-wheel" }, [this.gradientSvg, this.ringsSvg, this.hubBtn]);
    this.el = el("div", { class: "pw-overlay" }, [this.container, this.library, this.fx.el]);
    this.el.hidden = true;
    document.body.appendChild(this.el);

    this.buildRings();
    this.buildLibrary();
    this.bindHub();
    this.bindSecondary();
    this.bindPin();

    // Al abrir sin muestra activa, arranca en la primera del primer anillo.
    for (const ring of this.palette.rings) {
      if (ring.items.length) {
        this.active = ring.items[0];
        this.primaryHex = ring.items[0].hex;
        break;
      }
    }

    this.onDocDown = (e: PointerEvent) => {
      if (!this.visible) return;
      const t = e.target as Node;
      if (this.container.contains(t) || this.library.contains(t)) return;
      // Bloqueada (pulsación larga >3 s): el clic fuera NO hace nada; los niveles
      // solo se contraen con el clic manual en el hub. Se sale con otra larga.
      if (this.locked) return;
      if (this.pinned) {
        // Fijada (punto rosa arriba): el clic fuera solo CONTRAE el anillo de
        // colores; la bolita del hub queda a la vista para reabrirla.
        if (this.expanded) this.setExpanded(false);
      } else {
        // Sin fijar: el clic fuera la CIERRA del todo (también la bolita).
        this.hide();
      }
    };

    this.applyPosition();
    this.applyRotation();
    this.renderGradient();
    this.updateHub();
    this.setExpanded(false);

    // Reflejar estado inicial del pin (activado por defecto)
    this.pinDot.classList.toggle("is-pinned", this.pinned);
    this.pinDot.title = this.pinned ? "Fijada (clic para soltar)" : "Fijar (que no se cierre al hacer clic fuera)";
    // Reflejar estado inicial del bloqueo (pulsación larga). Persistido aparte.
    this.setLocked(this.locked, true);

    // Mostrar la rueda al iniciar (colapsada, en su posición por defecto).
    // Si venía bloqueada de otra sesión, arranca expandida (el bloqueo la
    // mantiene abierta).
    this.showCollapsed();
    if (this.locked) this.setExpanded(true);
  }

  /** Muestra el núcleo de la rueda sin expandir los anillos (estado inicial). */
  private showCollapsed(): void {
    this.visible = true;
    this.el.hidden = false;
    this.enterFx();
    this.setExpanded(false);
    document.addEventListener("pointerdown", this.onDocDown, true);
  }

  /**
   * Aparición con partículas (sistema "materia"): las gotas se juntan y forman el
   * hub, que se funde en relevo con la masa. Mismo motivo que el menú radial.
   */
  private enterFx(): void {
    window.clearTimeout(this.fxTimer);
    if (prefersReducedMotion()) {
      this.container.classList.remove("is-forming");
      return;
    }
    this.container.classList.add("is-forming"); // hub oculto mientras se forma
    this.fx.center(this.position.x, this.position.y);
    this.fx.gather();
    this.fxTimer = window.setTimeout(() => {
      if (!this.visible) return;
      this.container.classList.remove("is-forming"); // el hub se funde con la masa
      this.fx.fadeOut();
    }, this.fx.gatherMs);
  }

  /** Desaparición: el hub se desintegra en partículas que salen despedidas. */
  private exitFx(): void {
    window.clearTimeout(this.fxTimer);
    if (prefersReducedMotion()) return;
    this.container.classList.add("is-forming"); // el hub se apaga en el relevo
    this.fx.center(this.position.x, this.position.y);
    this.fx.scatter();
  }

  // ------------------------------------------------------------- construccion

  private buildRings(): void {
    while (this.rotGroup.firstChild) this.rotGroup.removeChild(this.rotGroup.firstChild);

    // Radio de referencia comun para el hueco angular: asi todos los niveles
    // comparten el mismo angulo de separacion y los bordes entre sectores caen
    // en lineas radiales rectas (sin desfase entre un anillo y el siguiente).
    const refR = this.palette.rings[0]?.radius ?? RING_THICKNESS;

    for (const ring of this.palette.rings) {
      const outer = ring.radius + RING_THICKNESS;
      for (const item of ring.items) {
        const mid = (item.startAngle + item.endAngle) / 2;
        const cen = polar(ring.radius + RING_THICKNESS / 2, mid);

        const path = svgEl("path", {
          class: "pw-swatch",
          d: annularSector(ring.radius, outer, item.startAngle, item.endAngle, GAP, 0, refR),
          fill: item.hex,
          stroke: item.hex,
          // Trazo fino del mismo color: solo tapa la costura de antialias entre
          // muestras contiguas (radial y angularmente) para que no asome el fondo.
          // Antes valia 4 y cada anillo pintaba 2px sobre el de dentro, desplazando
          // el color respecto a su etiqueta —la "superposicion" que se notaba—.
          "stroke-width": 1.5,
          "stroke-linejoin": "round",
        });
        path.addEventListener("pointerdown", (e) => this.onSwatchDown(e, item));
        path.addEventListener("pointerenter", () => this.updateHub(item));
        path.addEventListener("pointerleave", () => this.updateHub());
        this.rotGroup.appendChild(path);

        // Etiqueta en el centro del sector.
        const label = svgEl("text", {
          class: "pw-label",
          x: cen.x,
          y: cen.y,
          transform: `rotate(${textRotation(mid)} ${cen.x} ${cen.y})`,
          "text-anchor": "middle",
          "dominant-baseline": "central",
        });
        label.textContent = item.name;
        this.rotGroup.appendChild(label);
      }
    }
  }

  private renderGradient(): void {
    while (this.gradientSvg.firstChild) this.gradientSvg.removeChild(this.gradientSvg.firstChild);
    const activeHex = this.active?.hex ?? "#888888";
    const seg = 180 / GRAD_SEGMENTS;
    const outer = GRAD_RADIUS + GRAD_THICKNESS;

    const add = (hex: string, start: number, end: number): void => {
      const path = svgEl("path", {
        class: "pw-grad",
        d: annularSector(GRAD_RADIUS, outer, start, end, 2),
        fill: hex,
        stroke: hex,
        "stroke-width": 4,
        "stroke-linejoin": "round",
      });
      path.addEventListener("click", () => this.selectColor(hex));
      this.gradientSvg.appendChild(path);
    };

    // Mitad izquierda: escala de grises de blanco a negro.
    for (let i = 0; i < GRAD_SEGMENTS; i++) {
      const g = Math.round(255 * (1 - i / (GRAD_SEGMENTS - 1)));
      const h = g.toString(16).padStart(2, "0");
      add(`#${h}${h}${h}`, 180 + i * seg, 180 + (i + 1) * seg);
    }
    // Primer cuarto: negro al color activo.
    for (let i = 0; i < GRAD_SEGMENTS / 2; i++) {
      add(lerpHex("#000000", activeHex, i / (GRAD_SEGMENTS / 2 - 1)), i * seg, (i + 1) * seg);
    }
    // Segundo cuarto: color activo a blanco.
    for (let i = 0; i < GRAD_SEGMENTS / 2; i++) {
      add(lerpHex(activeHex, "#FFFFFF", i / (GRAD_SEGMENTS / 2 - 1)), 90 + i * seg, 90 + (i + 1) * seg);
    }
  }

  private buildLibrary(): void {
    this.library.textContent = "";
    this.library.appendChild(el("div", { class: "pw-lib-title", text: "Presets de color" }));

    const grid = el("div", { class: "pw-lib-grid" });
    for (const pal of [...PANTONE_PALETTES, ...this.customPalettes]) {
      const preview = [...pal.preview];
      while (preview.length < 4) preview.push(preview[preview.length - 1] ?? "#333");
      const swatches = el("span", { class: "pw-lib-preview" }, preview.slice(0, 4).map((c) => el("i", { style: { background: c } })));
      const btn = el("button", {
        class: "pw-lib-item",
        type: "button",
        title: pal.name,
        on: { click: () => this.setPalette(pal.id) },
      }, [swatches]);
      btn.dataset.pal = pal.id;
      grid.appendChild(btn);
    }

    // Cargar Pantones (.ase / .aco) y extraer paleta de una imagen.
    grid.appendChild(this.importButton("upload", "ASE / ACO", ".ase,.aco", (f) => this.importSwatchFile(f)));
    grid.appendChild(this.importButton("wheel", "Imagen", "image/*", (f) => this.importImage(f)));

    this.library.appendChild(grid);
    this.library.appendChild(el("div", { class: "pw-lib-status", id: "pw-lib-status" }));
    this.markLibrary();
  }

  private get statusEl(): HTMLElement | null {
    return this.library.querySelector(".pw-lib-status");
  }

  /** Muestra el estado de importacion (cargando / error) bajo la rejilla. */
  private setStatus(text: string, kind: "busy" | "error" | "" = ""): void {
    const s = this.statusEl;
    if (!s) return;
    s.textContent = "";
    s.className = `pw-lib-status${kind ? ` is-${kind}` : ""}`;
    if (kind === "busy") s.appendChild(el("span", { class: "pw-spinner" }));
    if (text) s.appendChild(el("span", { text }));
  }

  /** Espera dos fotogramas para que el navegador pinte el estado "cargando". */
  private nextPaint(): Promise<void> {
    return new Promise((resolve) =>
      requestAnimationFrame(() => requestAnimationFrame(() => resolve())),
    );
  }

  private importButton(iconName: string, label: string, accept: string, onFile: (f: File) => void): HTMLElement {
    const input = el("input", { class: "pw-lib-file", type: "file" }) as HTMLInputElement;
    input.accept = accept;
    input.addEventListener("change", () => {
      const f = input.files?.[0];
      if (f) onFile(f);
      input.value = "";
    });
    const btn = el("button", {
      class: "pw-lib-item pw-lib-import",
      type: "button",
      title: `Cargar ${label}`,
      on: { click: () => input.click() },
    }, [
      el("span", { class: "pw-lib-icon", html: icon(iconName) }),
      el("span", { class: "pw-lib-tag", text: label }),
      input,
    ]);
    return btn;
  }

  private addCustomPalette(pal: Palette): void {
    this.customPalettes.push(pal);
    this.paletteId = pal.id;
    this.palette = pal;
    localStorage.setItem(PAL_KEY, pal.id);
    this.setStatus("");
    this.buildLibrary();
    this.library.hidden = true;
    // Muestra la paleta recien cargada con la misma animacion de entrada que al
    // elegir una del listado: si la rueda estaba plegada se despliega (lo que ya
    // reinicia la animacion), y si estaba abierta se re-lanza a mano.
    if (this.expanded) {
      this.buildRings();
      this.replayEntrance();
    } else {
      this.setExpanded(true);
    }
  }

  private importSwatchFile(file: File): void {
    this.setStatus("Leyendo archivo…", "busy");
    const reader = new FileReader();
    reader.onerror = () => this.setStatus("No se pudo leer el archivo.", "error");
    reader.onload = async (e) => {
      const buffer = e.target?.result as ArrayBuffer;
      await this.nextPaint(); // deja que se pinte el "cargando" antes de parsear
      try {
        const lower = file.name.toLowerCase();
        const colors = lower.endsWith(".ase") ? parseASE(buffer) : lower.endsWith(".aco") ? parseACO(buffer) : [];
        if (colors.length === 0) {
          this.setStatus("Sin colores compatibles en el archivo.", "error");
          return;
        }
        this.addCustomPalette(createPaletteFromColors(file.name.replace(/\.[^/.]+$/, ""), colors));
      } catch {
        this.setStatus("Archivo .ase/.aco invalido.", "error");
      }
    };
    reader.readAsArrayBuffer(file);
  }

  private async importImage(file: File): Promise<void> {
    this.setStatus("Leyendo imagen…", "busy");
    try {
      const { data } = await decodeImagePixels(file);
      // El k-means es lo caro: se manda a un worker para que la rueda y el
      // spinner sigan animando en vez de quedarse congelados.
      this.setStatus("Extrayendo colores…", "busy");
      const colors = await this.clusterColors(data, 72, file);
      if (colors.length === 0) {
        this.setStatus("No se pudieron extraer colores.", "error");
        return;
      }
      this.addCustomPalette(createPaletteFromColors(file.name.replace(/\.[^/.]+$/, ""), colors));
    } catch {
      this.setStatus("Error al procesar la imagen.", "error");
    }
  }

  /** Corre el k-means en un worker; si no hay Worker, cae al hilo principal. */
  private clusterColors(data: Uint8ClampedArray, numColors: number, file: File): Promise<ImportedColor[]> {
    if (typeof Worker === "undefined") return extractColorsFromImage(file, numColors);
    try {
      if (!this.worker) {
        this.worker = new Worker(new URL("./pantone-kmeans.worker.ts", import.meta.url), { type: "module" });
      }
      const worker = this.worker;
      return new Promise<ImportedColor[]>((resolve, reject) => {
        const onMessage = (e: MessageEvent<{ colors?: ImportedColor[]; error?: string }>) => {
          cleanup();
          if (e.data.error) reject(new Error(e.data.error));
          else resolve(e.data.colors ?? []);
        };
        const onError = (err: ErrorEvent) => {
          cleanup();
          reject(err.error ?? new Error("Worker fallo"));
        };
        const cleanup = () => {
          worker.removeEventListener("message", onMessage as EventListener);
          worker.removeEventListener("error", onError as EventListener);
        };
        worker.addEventListener("message", onMessage as EventListener);
        worker.addEventListener("error", onError as EventListener);
        // Se transfiere el buffer: no se copia, pasa de dueno al worker.
        const buffer = data.buffer.slice(0);
        worker.postMessage({ data: buffer, numColors }, [buffer]);
      });
    } catch {
      return extractColorsFromImage(file, numColors);
    }
  }

  private markLibrary(): void {
    for (const b of Array.from(this.library.querySelectorAll(".pw-lib-item")) as HTMLElement[]) {
      if (b.dataset.pal) b.classList.toggle("is-active", b.dataset.pal === this.paletteId);
    }
  }

  private resolvePalette(id: string): Palette {
    return this.customPalettes.find((p) => p.id === id) ?? getPalette(id);
  }

  // --------------------------------------------------------------- seleccion

  private selectColor(hex: string): void {
    this.onColorSelect(hex);
  }

  private onSwatchDown(e: PointerEvent, item: Swatch): void {
    e.preventDefault();
    // Selecciona ya, en el pointerdown, para poder pintar sin esperar.
    this.active = item;
    this.selectColor(item.hex);
    this.updateHub(item);
    this.renderGradient();
    this.beginRotate(e);
  }

  private updateHub(hover?: Swatch): void {
    // Al pasar por una muestra, el hub la previsualiza; en reposo muestra el color
    // activo del editor (primaryHex), que reflect() mantiene al día.
    if (hover) {
      this.hubBtn.style.background = hover.hex;
      this.hubBtn.style.color = contrastText(hover.hex);
      this.hubName.textContent = hover.name;
      this.hubHex.textContent = hover.hex.toUpperCase();
      return;
    }
    const hex = this.primaryHex;
    this.hubBtn.style.background = hex;
    this.hubBtn.style.color = contrastText(hex);
    const match = this.active && this.active.hex.toLowerCase() === hex.toLowerCase();
    this.hubName.textContent = match ? (this.active?.name ?? "") : "";
    this.hubHex.textContent = hex.toUpperCase();
  }

  /**
   * Refleja los colores del editor: el hub pinta el primario y el círculo
   * sobrepuesto el secundario. Lo llama la App en cada cambio de estado, así el
   * intercambio con la tecla X o el clic en el círculo se ve al instante.
   */
  reflect(primary: string, secondary: string): void {
    this.primaryHex = primary;
    this.secondaryBtn.style.background = secondary;
    this.secondaryBtn.title = `Secundario ${secondary.toUpperCase()} · clic para intercambiar (X)`;
    this.updateHub();
  }

  // ----------------------------------------------------------------- rotacion

  private angleFromCenter(clientX: number, clientY: number): number {
    const r = this.ringsSvg.getBoundingClientRect();
    return (Math.atan2(clientY - (r.top + r.height / 2), clientX - (r.left + r.width / 2)) * 180) / Math.PI;
  }

  private beginRotate(e: PointerEvent): void {
    this.stopMomentum();
    this.rotating = true;
    this.velocity = 0;
    this.lastAngle = this.angleFromCenter(e.clientX, e.clientY);

    const move = (ev: PointerEvent): void => {
      if (!this.rotating) return;
      const a = this.angleFromCenter(ev.clientX, ev.clientY);
      let d = a - this.lastAngle;
      if (d > 180) d -= 360;
      if (d < -180) d += 360;
      this.velocity = d * 0.6 + this.velocity * 0.4;
      this.lastAngle = a;
      this.rotation += d;
      this.applyRotation();
    };
    const up = (): void => {
      this.rotating = false;
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
      if (Math.abs(this.velocity) > 0.5) this.momentum();
      else this.saveRotation();
    };
    window.addEventListener("pointermove", move, { passive: true });
    window.addEventListener("pointerup", up);
  }

  private momentum = (): void => {
    // Friccion con un punto de resorte, como el original: frena con un rebote.
    this.velocity = (this.velocity - this.velocity * 0.15) * 0.92;
    if (Math.abs(this.velocity) > 0.05) {
      this.rotation += this.velocity;
      this.applyRotation();
      this.raf = requestAnimationFrame(this.momentum);
    } else {
      this.velocity = 0;
      this.raf = 0;
      this.saveRotation();
    }
  };

  private stopMomentum(): void {
    if (this.raf) cancelAnimationFrame(this.raf);
    this.raf = 0;
    this.velocity = 0;
  }

  private applyRotation(): void {
    this.rotGroup.setAttribute("transform", `rotate(${this.rotation})`);
  }

  private saveRotation(): void {
    localStorage.setItem(ROT_KEY, String(this.rotation));
  }

  // ------------------------------------------------------------------- nucleo

  private bindHub(): void {
    let startX = 0;
    let startY = 0;
    let moved = false;

    this.hubBtn.addEventListener("pointerdown", (e) => {
      if (e.ctrlKey || e.metaKey) {
        e.preventDefault();
        this.toggleLibrary();
        return;
      }
      e.preventDefault();
      startX = e.clientX;
      startY = e.clientY;
      moved = false;
      const start = { ...this.position };

      // Pulsación larga (>3 s) sobre el hub: fija la rueda abierta o la cierra,
      // igual que mantener pulsado un sector del radial arranca sus chips.
      this.armLongPress();

      const move = (ev: PointerEvent): void => {
        const dx = ev.clientX - startX;
        const dy = ev.clientY - startY;
        if (Math.abs(dx) > LONG_PRESS_MOVE_TOL || Math.abs(dy) > LONG_PRESS_MOVE_TOL) {
          moved = true;
          this.cancelLongPress(); // arrastrar cancela la pulsación larga
        }
        if (moved) {
          this.position = { x: start.x + dx, y: start.y + dy };
          this.applyPosition();
        }
      };
      const up = (): void => {
        window.removeEventListener("pointermove", move);
        window.removeEventListener("pointerup", up);
        this.cancelLongPress();
        if (this.longPressFired) return; // la pulsación larga ya actuó
        if (moved) this.savePosition();
        else this.setExpanded(!this.expanded); // clic simple: abrir/cerrar (minimizar)
      };
      window.addEventListener("pointermove", move);
      window.addEventListener("pointerup", up);
    });
  }

  /** Arma la pulsación larga del hub. Al cumplirse, fija o cierra la rueda. */
  private armLongPress(): void {
    this.cancelLongPress();
    this.longPressFired = false;
    this.hubBtn.classList.add("is-charging");
    this.longPressTimer = window.setTimeout(() => {
      this.longPressFired = true;
      this.hubBtn.classList.remove("is-charging");
      this.onLongPress();
    }, LONG_PRESS_MS);
  }

  private cancelLongPress(): void {
    if (this.longPressTimer) {
      window.clearTimeout(this.longPressTimer);
      this.longPressTimer = 0;
    }
    this.hubBtn.classList.remove("is-charging");
  }

  /**
   * Efecto de la pulsación larga: si la rueda ya está fijada, la cierra del todo
   * (y la suelta); si no, la fija abierta por completo. Un solo gesto para las
   * dos cosas, como pidió el usuario.
   */
  /**
   * Efecto de la pulsación larga (>3 s): ALTERNA el bloqueo. Bloqueada, la rueda
   * queda abierta e ignora los clics de fuera —solo el clic manual en el hub
   * contrae/expande los niveles— y sigue así hasta otra pulsación larga. Un solo
   * gesto para las dos cosas.
   */
  private onLongPress(): void {
    if (typeof navigator !== "undefined" && navigator.vibrate) navigator.vibrate(20);
    this.setLocked(!this.locked);
  }

  /**
   * Activa/desactiva el bloqueo por pulsación larga y lo persiste. Al bloquear,
   * asegura que la rueda esté visible y expandida y la deja fijada (para que al
   * desbloquear vuelva al modo "fijada": el clic fuera contrae, no cierra). El
   * clic normal en el hub NO toca este estado, así que el bloqueo permanece.
   * `silent` solo refleja el estado inicial sin forzar visibilidad.
   */
  private setLocked(on: boolean, silent = false): void {
    this.locked = on;
    localStorage.setItem(LOCK_KEY, String(on));
    this.pinDot.classList.toggle("is-locked", on);
    this.hubBtn.classList.toggle("is-locked", on);
    if (on) {
      this.pinDot.title = "Bloqueada (pulsación larga para soltar)";
      if (!silent) {
        this.setPinned(true);
        if (!this.visible) this.show();
        else this.setExpanded(true);
      }
    } else if (!silent) {
      this.pinDot.title = this.pinned ? "Fijada (clic para soltar)" : "Fijar (que no se cierre al hacer clic fuera)";
    }
  }

  /** El círculo secundario: no arrastra ni abre la rueda; solo intercambia. */
  private bindSecondary(): void {
    this.secondaryBtn.addEventListener("pointerdown", (e) => e.stopPropagation());
    this.secondaryBtn.addEventListener("click", (e) => {
      e.stopPropagation();
      this.onSwap();
    });
  }

  private bindPin(): void {
    this.pinDot.addEventListener("pointerdown", (e) => e.stopPropagation());
    this.pinDot.addEventListener("click", (e) => {
      e.stopPropagation();
      this.setPinned(!this.pinned);
    });
  }

  /** Fija o suelta la rueda (persistente) y refleja el estado en el punto del pin. */
  private setPinned(on: boolean): void {
    this.pinned = on;
    this.pinDot.classList.toggle("is-pinned", this.pinned);
    // Si está bloqueada (pulsación larga), su título manda sobre el del pin.
    if (!this.locked) {
      this.pinDot.title = this.pinned ? "Fijada (clic para soltar)" : "Fijar (que no se cierre al hacer clic fuera)";
    }
    localStorage.setItem(PIN_KEY, String(this.pinned));
  }

  private applyPosition(): void {
    this.container.style.left = `${this.position.x}px`;
    this.container.style.top = `${this.position.y}px`;
  }

  private savePosition(): void {
    localStorage.setItem(POS_KEY, JSON.stringify(this.position));
  }

  private setExpanded(on: boolean): void {
    this.expanded = on;
    window.clearTimeout(this.collapseTimer);
    // SVGSVGElement no tipa `hidden`; el atributo cae bajo [hidden] en el CSS.
    if (on) {
      this.ringsSvg.classList.remove("is-leaving");
      this.gradientSvg.classList.remove("is-leaving");
      // Quitar `hidden` (display: none -> visible) reinicia la animacion -in.
      this.ringsSvg.toggleAttribute("hidden", false);
      this.gradientSvg.toggleAttribute("hidden", false);
      this.buildRings();
      this.container.classList.add("is-expanded");
    } else {
      // Deja correr la animacion de salida antes de esconder los anillos.
      this.ringsSvg.classList.add("is-leaving");
      this.gradientSvg.classList.add("is-leaving");
      this.container.classList.remove("is-expanded");
      this.collapseTimer = window.setTimeout(() => {
        this.ringsSvg.toggleAttribute("hidden", true);
        this.gradientSvg.toggleAttribute("hidden", true);
        this.ringsSvg.classList.remove("is-leaving");
        this.gradientSvg.classList.remove("is-leaving");
      }, 160);
    }
  }

  private setPalette(id: string): void {
    this.paletteId = id;
    this.palette = this.resolvePalette(id);
    localStorage.setItem(PAL_KEY, id);
    this.buildRings();
    this.replayEntrance();
    this.markLibrary();
    this.library.hidden = true;
  }

  /**
   * Vuelve a lanzar la animacion de entrada de los anillos.
   *
   * La animacion `-in` solo corre al aparecer el elemento; al cambiar de paleta
   * el SVG ya esta en pantalla, asi que se reinicia a mano: se quita la
   * animacion, se fuerza un reflow y se restaura, para que la nueva paleta entre
   * escalando desde el centro igual que la primera vez.
   */
  private replayEntrance(): void {
    if (!this.expanded) return;
    for (const svg of [this.ringsSvg, this.gradientSvg]) {
      svg.classList.remove("is-leaving");
      svg.style.animation = "none";
      void svg.getBoundingClientRect(); // fuerza reflow para reiniciar la animacion
      svg.style.animation = "";
    }
  }

  private toggleLibrary(): void {
    this.library.hidden = !this.library.hidden;
  }

  // -------------------------------------------------------------------- api

  get isVisible(): boolean {
    return this.visible;
  }

  show(): void {
    if (this.visible) return;
    window.clearTimeout(this.hideTimer);
    this.visible = true;
    this.el.hidden = false;
    // Si quedo fuera de pantalla (cambio de resolucion), recentrar.
    if (
      this.position.x < 40 || this.position.y < 40 ||
      this.position.x > window.innerWidth - 40 || this.position.y > window.innerHeight - 40
    ) {
      this.position = { x: window.innerWidth / 2, y: window.innerHeight / 2 };
      this.applyPosition();
    }
    // Aparición con partículas (sistema "materia") + expansión de los anillos.
    this.enterFx();
    this.setExpanded(true);
    document.addEventListener("pointerdown", this.onDocDown, true);
  }

  hide(): void {
    if (!this.visible) return;
    this.visible = false;
    this.library.hidden = true;
    this.stopMomentum();
    document.removeEventListener("pointerdown", this.onDocDown, true);
    // Anima la salida: anillos se recogen y el hub se desintegra en partículas.
    this.setExpanded(false);
    this.exitFx();
    window.clearTimeout(this.hideTimer);
    this.hideTimer = window.setTimeout(() => {
      this.el.hidden = true;
      this.container.classList.remove("is-forming");
      this.fx.clear();
    }, this.fx.scatterMs);
  }

  toggle(): void {
    if (this.visible) this.hide();
    else this.show();
  }

  /**
   * Alterna desde la tecla R (y la acción "Rueda" del radial) RESPETANDO el pin,
   * igual que el clic fuera: fijada, solo contrae/expande el anillo de colores y
   * la bolita queda; sin fijar, muestra/oculta la rueda entera.
   */
  keyToggle(): void {
    if (!this.pinned) {
      this.toggle();
      return;
    }
    if (!this.visible) this.show();
    else this.setExpanded(!this.expanded);
  }

  /** ¿La rueda está a la vista y expandida (no solo la bolita)? Lo usa el HUD
      para avisar si un toggle la abrió o la cerró. */
  get isShowing(): boolean {
    return this.visible && this.expanded;
  }

  /** Abre la rueda expandida. Si ya está a la vista (aunque plegada), la despliega.
      Lo usan el botón del dock y, como alternativa, la acción "Rueda" del radial. */
  open(): void {
    if (!this.visible) this.show();
    else this.setExpanded(true);
  }

  dispose(): void {
    this.stopMomentum();
    this.cancelLongPress();
    window.clearTimeout(this.collapseTimer);
    window.clearTimeout(this.hideTimer);
    document.removeEventListener("pointerdown", this.onDocDown, true);
    this.worker?.terminate();
    this.worker = null;
    this.el.remove();
  }
}
